import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Crown, Lock, BookOpen, Download, Shield, Calendar } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { downloadEbook } from "@/lib/checkout";

function PostCard({ p }) {
  return (
    <article className="panel p-5" data-testid={`member-post-${p.id}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] font-bold tracking-widest text-smasher-orange bg-smasher-orange/10 px-2 py-0.5 rounded">{p.category}</span>
        <span className={`text-[10px] font-black tracking-widest ${p.tier === "pro" ? "text-smasher-gold" : "text-slate-400"}`}>{p.tier === "pro" ? "PRO" : "STARTER"}</span>
      </div>
      <h3 className="font-heading font-bold text-xl text-white">{p.title}</h3>
      <p className="text-sm text-slate-300 mt-2 leading-relaxed whitespace-pre-line">{p.body}</p>
      <div className="text-[11px] text-slate-500 mt-3 flex items-center gap-1"><Calendar className="w-3 h-3" /> {new Date(p.created_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</div>
    </article>
  );
}

function EbookPanel({ owned, viaPro }) {
  return (
    <div className="panel p-5 flex items-center justify-between gap-4" data-testid="members-ebook-panel">
      <div className="flex items-center gap-3"><BookOpen className="w-6 h-6 text-smasher-gold" />
        <div><div className="font-heading font-bold text-white">Football Edition Ebook</div>
          <div className="text-xs text-slate-400">{viaPro ? "Included free with Pro Smasher" : owned ? "In your library" : "Not purchased yet — free with Pro Smasher"}</div></div>
      </div>
      {owned ? (
        <button onClick={downloadEbook} className="btn-gold px-4 py-2 rounded-lg font-bold text-sm inline-flex items-center gap-2" data-testid="members-ebook-download"><Download className="w-4 h-4" /> DOWNLOAD</button>
      ) : (
        <Link to="/ebook" className="btn-ghost-gold px-4 py-2 rounded-lg font-bold text-sm" data-testid="members-ebook-buy">GET IT</Link>
      )}
    </div>
  );
}

function Upsell() {
  return (
    <div className="panel p-10 text-center" data-testid="members-locked">
      <Lock className="w-10 h-10 text-smasher-gold mx-auto mb-3" />
      <h2 className="font-heading font-black text-2xl text-white">Members-only content</h2>
      <p className="text-slate-400 mt-2 max-w-md mx-auto text-sm">Daily best bets, analysis and premium picks unlock with a Starter Smash or Pro Smasher membership.</p>
      <Link to="/membership" className="btn-gold inline-block mt-6 px-6 py-3 rounded-xl font-heading font-black" data-testid="members-view-plans">VIEW PLANS</Link>
    </div>
  );
}

export default function MembersPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [ebook, setEbook] = useState({ owned: false });
  const isMember = user.role === "admin" || ["starter", "pro"].includes(user.membership);

  useEffect(() => {
    api.get("/ebook/status").then((r) => setEbook(r.data)).catch(() => {});
    if (isMember) api.get("/members/posts").then((r) => setData(r.data)).catch(() => setData({ posts: [] }));
  }, [isMember]);

  const tier = user.role === "admin" ? "pro" : user.membership;
  return (
    <div className="max-w-[1100px] mx-auto px-4 sm:px-6 py-12 space-y-8">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs font-bold tracking-[0.2em] text-smasher-orange mb-1">MY AREA</div>
          <h1 className="font-heading font-black uppercase text-4xl sm:text-5xl text-gradient-gold">Welcome, {user.name || "Smasher"}</h1>
        </div>
        <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-full font-black text-sm ${tier === "pro" ? "btn-gold" : "bg-white/5 text-slate-300 border border-white/10"}`} data-testid="members-tier-badge">
          {tier === "pro" ? <Crown className="w-4 h-4" /> : <Shield className="w-4 h-4" />} {tier === "pro" ? "PRO SMASHER" : tier === "starter" ? "STARTER SMASH" : "FREE ACCOUNT"}
        </span>
      </div>
      <EbookPanel owned={ebook.owned} viaPro={ebook.via_pro} />
      {!isMember ? <Upsell /> : (
        <section className="space-y-4">
          <h2 className="font-heading font-black uppercase text-2xl text-white">Member Feed</h2>
          {data?.posts?.length ? data.posts.map((p) => <PostCard key={p.id} p={p} />) : (
            <div className="panel p-8 text-center text-slate-500" data-testid="members-feed-empty">{data ? "No member posts yet — the next breakdown drops soon." : "Loading…"}</div>
          )}
          {data?.locked_count > 0 && (
            <Link to="/membership" className="panel p-4 flex items-center gap-3 text-sm text-slate-300 hover:text-smasher-gold" data-testid="members-pro-upsell">
              <Lock className="w-4 h-4 text-smasher-gold" /> {data.locked_count} Pro-only post{data.locked_count > 1 ? "s" : ""} — upgrade to Pro Smasher to unlock.
            </Link>
          )}
        </section>
      )}
    </div>
  );
}
